import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useLocales } from "src/locales";
import { Incrementer } from "../shop";
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Chip, Typography, useTheme } from "@mui/material";
import { fTime } from "src/utils/formatTime";
import useResponsive from "src/hooks/useResponsive";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Image from "src/components/image";
import { fCurrency } from "src/utils/formatNumber";
import Iconify from "src/components/iconify";
import { PrimaryButton } from "../listing-details/listingButtons";
import { TICKET_CONSTANTS } from "src/config/constants/ticket-constants";
import Markdown from "src/components/Markdown";
import { currencyMap } from "src/utils/currencies";


export default function RecurringTicketShopCard({
    sessions,
    cart,
    event,
    initialTicketQuantities,
    updateTicketQuantities
}) {

    const [totalItems, setTotalItems] = useState(0);
    const theme = useTheme();

    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useResponsive("down", "sm");
    const [cloudQuantity, setCloudQuantity] = useState([]);
    const [localQuantity, setLocalQuantity] = useState(
        initialTicketQuantities || []
    );

    const [expandedSessions, setExpandedSessions] = useState(new Set());

    const currency = currencyMap[event?.general?.currency];

    useEffect(() => {

        const newLocalQuantity = [];
        const newCloudQuantity = [];

        const apiCartItems = cart?.items;

        let latestTotalItems = 0;

        if (apiCartItems?.length > 0) {
            cart?.items?.forEach((item) => {
                const session = sessions.find((s) => s.id === item?.eventSession?.id);
                const isTicketInTicketTypes = session?.ticketTypes.find(
                    (ticketType) => ticketType.id === item?.ticketType?.id
                );

                if (item?.ticketType) {
                    latestTotalItems += item.quantity;
                }

                if (isTicketInTicketTypes) {
                    newLocalQuantity.push({
                        id: item?.ticketType?.id,
                        sessionId: session.id,
                        quantity: item.quantity || 0,
                        price: item?.ticketType?.price || 0,
                        seatIds: item?.seatIds,
                        seatsHoldToken: item?.seatsHoldToken,
                        name: item?.ticketType?.name,
                    });
                    newCloudQuantity.push({
                        quantity: item.quantity || 0,
                        id: item.id,
                        sessionId: session.id,
                        ticketId: item?.ticketType?.id,
                        seatIds: item?.seatIds,
                        seatsHoldToken: item?.seatsHoldToken,
                    });
                }
            });

            setTotalItems(latestTotalItems);
            setCloudQuantity(newCloudQuantity);
            setLocalQuantity(newLocalQuantity);

            // checkClearCartModalStatus(apiCartItems);
        } else {
            setTotalItems(0);
        }

    }, [cart])

    useEffect(() => {
        if (updateTicketQuantities) updateTicketQuantities(localQuantity);
    }, [cloudQuantity, localQuantity]);

    const decrementQuantity = (id, sessionId) => {
        const updatedQuantities = localQuantity
            .map((item) => {
                if (item.id === id && item.sessionId === sessionId) {
                    item.quantity--;
                    if (item.quantity === 0) {
                        return null;
                    }
                }
                return item;
            })
            .filter(Boolean);

        setLocalQuantity(updatedQuantities);
    };

    const incrementQuantity = (ticket, sessionId) => {
        const existingItem = localQuantity.find((item) => item.id === ticket.id && item.sessionId === sessionId);

        if (existingItem) {
            const updatedQuantities = localQuantity.map((item) => {
                if (item.id === ticket.id && item.sessionId === sessionId) {
                    return { ...item, quantity: item.quantity + 1 };
                }
                return item;
            });
            setLocalQuantity(updatedQuantities);
        } else {
            setLocalQuantity([
                ...localQuantity,
                {
                    id: ticket.id,
                    sessionId: sessionId,
                    price: ticket.price,
                    quantity: 1,
                    name: ticket.name,
                    seatIds: [],
                    seatsHoldToken: ticket.seatsHoldToken,
                },
            ]);
        }
    };

    const isTicketAvailable = (ticket) => {
        return Boolean(ticket.available);
    };

    const getTotalQty = () =>
        localQuantity.reduce((total, item) => total + item.quantity, 0);

    const getTotalCloudQty = () =>
        cloudQuantity.reduce((total, item) => total + item.quantity, 0);

    const getIncrementer = (ticket, session) => {
        if (!isTicketAvailable(ticket)) {
            return <Chip label={translate("productPage.soldOut")} />;
        }

        if (ticket.category.name === "Earlybird") {
            return <Chip color="light" label={translate("productPage.comingSoon")} />;
        }

        if (isTicketAvailable(ticket)) {
            const sessionQuantity = localQuantity.filter(item => item.sessionId === session.id).reduce((acc, it) => acc + it.quantity, 0)
            const ticketTypeQuantity = localQuantity.filter(item => item.id === ticket.id).reduce((acc, it) => acc + it.quantity, 0)
                return(
                    <Incrementer
                        available={Math.min(ticket.available, session.availableCapacity)}
                        totalItems={totalItems}
                        totalQty={getTotalQty()}
                        quantity={
                            localQuantity.find((item) => item.id === ticket.id && item.sessionId === session.id)?.quantity || 0
                        }
                        onDecrementQuantity={() => {
                            decrementQuantity(ticket.id, session.id);
                        }}
                        onIncrementQuantity={() => {
                            incrementQuantity(ticket, session.id);
                        }}
                        initialQuantity={getTotalCloudQty()}
                        maxQuantity={TICKET_CONSTANTS.MAX_CART_QUANTITY}
                        sessionQuantity={sessionQuantity}
                        maxSessionQuantity={session.availableCapacity}
                        ticketTypeQuantity={ticketTypeQuantity}
                        maxTicketTypeQuantity={ticket.available}
                    />
                );
        }
    };

    const isPastMaxValue = useMemo(() => {
        return (
            totalItems - getTotalCloudQty() + getTotalQty() >=
            TICKET_CONSTANTS.MAX_CART_QUANTITY
        );
    }, [totalItems, localQuantity, cloudQuantity]);

    const getMinimumPromotionPrice = (ticketTypes) => {
        const tieredTickets = ticketTypes.filter(
            (ticket) => ticket.promotionType === "TIERED"
        );
        const allPrices = tieredTickets.flatMap((ticket) =>
            ticket.promotionMetadata.map((promo) => promo.price)
        );
        if (allPrices.length === 0) return null;
        return Math.min(...allPrices);
    };

    const toggleSession = (id) => {
        setExpandedSessions((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const TicketCard = ({ ticket, session }) => {
        return (
            (ticket.isPublished &&
                (!ticket.promotionType ||
                    ticket.promotionType === "NO_PROMOTION") && (
                    <Card
                        key={ticket.id}
                        raised
                        sx={{
                            mt: 2,
                            backgroundColor: theme.palette.grey[800],
                            borderRadius: "8px",
                        }}
                    >
                        <CardContent
                            sx={{
                                p: 2,
                                pb: "0 !important",
                                display: "flex",
                                justifyContent: "space-between",
                                minHeight: 100,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography
                                    variant="h6"
                                >
                                    {ticket.name}
                                </Typography>
                                <Markdown children={ticket.description} />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontFamily="Visby"
                                    align="right"
                                    style={{
                                        paddingRight: 6,
                                        marginBottom: 6,
                                    }}
                                    component={"div"}
                                >
                                    {ticket.price ? `${fCurrency(ticket.price)} ${currency}` : translate('eventManager.free')}
                                </Typography>
                                {getIncrementer(ticket, session)}
                            </div>
                        </CardContent>
                    </Card>
                )) ||
            (ticket.isPublished &&
                ticket.promotionType &&
                ticket.promotionType !== "NO_PROMOTION" && (
                    <Card
                        key={ticket.id}
                        raised
                        sx={{
                            mt: 2,
                            backgroundColor: theme.palette.grey[800],
                            borderRadius: "8px",
                        }}
                    >
                        <CardContent
                            sx={{
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 100,
                            }}
                        >
                            <Box sx={styles.rowBox}>
                                <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minWidth: 0 }}>
                                    <Typography
                                        style={{
                                            fontSize: 12,
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {translate("productPage.onlyinApp")}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        style={{
                                            marginTop: 5,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {ticket?.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", flex: "0 0 auto" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        {ticket.promotionType === "ONLY_IN_APP" && (
                                            <Typography
                                                variant="subtitle1"
                                                fontFamily="Visby"
                                                align="right"
                                                style={{
                                                    paddingRight: 12,
                                                    marginBottom: 6,
                                                    textDecoration: "line-through",
                                                    color: theme.palette.grey[500],
                                                }}
                                                component={"div"}
                                            >
                                                {`${fCurrency(
                                                    ticket.promotionMetadata.normalPrice
                                                )} ${currency}`}
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="subtitle1"
                                            fontFamily="Visby"
                                            align="right"
                                            style={{
                                                paddingRight: 6,
                                                marginBottom: 6,
                                                ...(!isTicketAvailable(ticket) && {
                                                    textDecoration: "line-through",
                                                }),
                                            }}
                                            component={"div"}
                                        >{ticket.price ? `${fCurrency(
                                            ticket.price
                                        )} ${currency}` : translate('eventManager.free')}</Typography>
                                    </Box>
                                    <Box>

                                    </Box>
                                    <PrimaryButton
                                        size="sm"
                                        sx={{ flex: 1 }}
                                        onClick={() => {
                                            window.location.href = `https://nightz.app/event/${event.general.id}`;
                                        }}
                                    >
                                        {translate("productPage.redeem")}
                                    </PrimaryButton>
                                </Box>
                            </Box>

                            {ticket.promotionType === "TIERED" && (
                                <Box
                                    style={{
                                        ...styles.avatarsAndProgressRow,
                                        flexDirection: isMobile ? "column" : "row",
                                        marginTop: isMobile ? 0 : 10,
                                        gap: theme.spacing(3.75),
                                    }}
                                >
                                    <Box
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            marginTop: theme.spacing(1),
                                        }}
                                    >
                                        <Image
                                            src="/assets/avatars/avatar1.png"
                                            sx={{ ...styles.image }}
                                        />
                                        <Image
                                            src="/assets/avatars/avatar2.png"
                                            sx={{ ...styles.image, marginLeft: -1 }}
                                        />
                                        <Image
                                            src="/assets/avatars/avatar3.png"
                                            sx={{ ...styles.image, marginLeft: -1 }}
                                        />
                                    </Box>
                                    <Box
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            paddingHorizontal: 16,
                                            justifyContent: "space-between",
                                            gap: theme.spacing(3),
                                        }}
                                    >
                                        <Box
                                            style={{
                                                width: "223px",
                                                height: theme.spacing(6.25),
                                                padding: 5,
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    backgroundImage:
                                                        "linear-gradient(to right, rgba(167, 167, 167, 0.3), white)",
                                                    borderRadius: "25px",
                                                    border: `solid 1px ${theme.palette.grey[500]}`,
                                                    height: "14px",
                                                    paddingTop: 10,
                                                }}
                                            />
                                            <Box
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    justifyContent: "space-between",
                                                    marginTop: theme.spacing(1),
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        color: theme.palette.grey[500],
                                                    }}
                                                >
                                                    <Iconify icon="octicon:person-16" />
                                                </Box>
                                                {ticket.promotionMetadata
                                                    .slice()
                                                    .sort(
                                                        (a, b) => a.nrParticipants - b.nrParticipants
                                                    )
                                                    .map((it, i) => (
                                                        <Box
                                                            style={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                color:
                                                                    ticket.promotionMetadata.length !==
                                                                        i + 1
                                                                        ? theme.palette.grey[500]
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <Typography>{it.nrParticipants}</Typography>
                                                            <Iconify icon={"octicon:person-16"} />
                                                        </Box>
                                                    ))}
                                            </Box>
                                        </Box>
                                        <Box style={{}}>
                                            <Typography
                                                style={{
                                                    color: theme.palette.text.primary,
                                                    fontFamily: "Visby",
                                                    fontSize: theme.spacing(3),
                                                    fontWeight: "500",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    lineHeight: theme.spacing(3),
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: theme.spacing(3),
                                                            fontWeight: "500",
                                                            fontFamily: "Visby",
                                                        }}
                                                    >
                                                        {getMinimumPromotionPrice(session?.ticketTypes)}
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: theme.spacing(2.5),
                                                            fontFamily: "Visby",
                                                        }}
                                                    >
                                                        {currency}
                                                    </span>
                                                </div>
                                                <Typography
                                                    style={{
                                                        color: theme.palette.text.primary,
                                                        fontFamily: "Visby",
                                                        fontSize: theme.spacing(2),
                                                    }}
                                                >
                                                    /pers.
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))
        )
    }


    return (
        <Box sx={{ mt: 2 }}>
            {sessions.map(session => {
                const isOpen = expandedSessions.has(session.id);

                return (
                    <Accordion
                        key={session.id}
                        expanded={isOpen}
                        disabled={!session.availableCapacity}
                        onChange={() => toggleSession(session.id)}
                        disableGutters
                        square
                        sx={{ ...flatAccordion, '&&': { mb: 2 } }}
                    >
                        <AccordionSummary sx={noJumpSummary}>

                            <Box sx={{
                                display: "inline-flex",
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: theme.palette.background.category,
                                borderRadius: 1,
                                paddingY: 2,
                                paddingX: 2
                            }}
                            >
                                <Box>
                                    <Typography variant="h6" component="div">
                                        {session.name}{session.name ? " | " : ""}{fTime(session.startDate.replace("Z", ""))}
                                    </Typography>
                                    <Typography variant="body1" component="div">
                                        {session.availableCapacity} {translate('tickets.spotsLeft')}
                                    </Typography>
                                </Box>
                                <ExpandMoreIcon
                                    sx={{
                                        transition: "transform 150ms ease",
                                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                />
                            </Box>

                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0, pb: 2, paddingX: 2 }}>
                            {session?.ticketTypes.map((ticket) => <TicketCard ticket={ticket} session={session} />)}
                        </AccordionDetails>
                    </Accordion>)
            })}
        </Box>

    )
}

const styles = {
    rowBox: {
        display: "flex",
        flexDirection: "row",
        paddingHorizontal: 2,
        gap: 2,
        justifyContent: "space-between",
    },
    avatarsAndProgressRow: {
        display: "flex",
        justifyContent: "space-between",
        paddingVertical: 8,
    },
    image: {
        width: 30,
        height: 30,
        borderRadius: 30,
    },

};

const flatAccordion = {
    '&&': {
        boxShadow: 'none',
        backgroundColor: 'transparent',
        borderRadius: 0,
        m: 0,
    },
    '&:before, &::before': { display: 'none' },

    '&&.Mui-expanded': {
        boxShadow: 'none',
        backgroundColor: 'transparent',
        borderRadius: 0,
        margin: 0,
    },
};

const noJumpSummary = {
    px: 0,
    minHeight: 56,
    alignItems: 'center',
    '&.Mui-expanded': { minHeight: 56 },
    '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
        margin: 0,
    },
    '&:hover': { backgroundColor: 'transparent' },
}
